import { motion } from 'motion/react';
import { Sparkles, Gem, ArrowRight, LogIn } from 'lucide-react';
import { theme } from '../../styles/theme';
import { useIsMobile } from '../../hooks/useIsMobile';
import connieGlobe from '../../assets/connie-globe.png';

interface WelcomePageProps {
  onNext: () => void;
  isAuthenticated: boolean;
  onShowSignIn: () => void;
}

export function WelcomePage({ onNext, isAuthenticated, onShowSignIn }: WelcomePageProps) {
  const mobile = useIsMobile();

  const handleButtonClick = () => {
    if (isAuthenticated) {
      onNext();
    } else {
      onShowSignIn();
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        display: 'flex',
        flexDirection: mobile ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: mobile ? '1.5rem' : '2rem',
        minHeight: mobile ? undefined : 'calc(100vh - 200px)',
        maxHeight: mobile ? undefined : 'calc(100vh - 160px)',
        overflow: 'auto',
        padding: mobile ? '1rem' : '2rem',
      }}
    >
      {/* Connie image */}
      <motion.div
        initial={{ x: mobile ? 0 : -40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        style={{
          flex: mobile ? undefined : '0 1 40%',
          maxWidth: mobile ? '200px' : '500px',
          height: mobile ? undefined : '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={connieGlobe}
          alt="Connie"
          style={{ width: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      </motion.div>

      {/* Text + CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{
          flex: mobile ? undefined : '0 1 50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: mobile ? 'center' : undefined,
          textAlign: mobile ? 'center' : undefined,
          gap: mobile ? theme.spacing.lg : theme.spacing['2xl'],
        }}
      >
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: theme.spacing.sm,
          padding: `${theme.spacing.xs} ${theme.spacing.md}`,
          borderRadius: theme.borderRadius.full,
          border: `1px solid ${theme.colors.tealBorder}`,
          background: theme.colors.tealBg,
          color: theme.colors.teal,
          fontSize: theme.fontSize.xs,
          fontWeight: theme.fontWeight.bold,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          width: 'fit-content',
        }}>
          <Gem size={12} /> Magical Concierge Service
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Sparkles size={14} color={theme.colors.teal} />
          </motion.div>
        </div>

        <h1 style={{
          fontFamily: theme.fontFamily.display,
          fontSize: mobile ? '2rem' : '3.5rem',
          fontWeight: theme.fontWeight.light,
          color: theme.colors.navy,
          letterSpacing: '-0.03em',
          lineHeight: 0.95,
          margin: 0,
        }}>
          I'm{' '}
          <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>
            Connie.
          </span>
          <br />
          Your personal travel guide.
        </h1>

        <p style={{
          fontSize: mobile ? theme.fontSize.base : theme.fontSize.xl,
          color: theme.colors.textMuted,
          fontWeight: theme.fontWeight.light,
          lineHeight: 1.7,
          margin: 0,
        }}>
          Allow me to weave a bit of magic into your next journey.
          Together, we'll curate an experience that is uniquely yours.
        </p>

        <button
          className={isAuthenticated ? 'btn-primary' : undefined}
          onClick={handleButtonClick}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: theme.spacing.md,
            padding: `${theme.spacing.lg} ${theme.spacing['3xl']}`,
            background: isAuthenticated ? undefined : 'white',
            color: isAuthenticated ? undefined : theme.colors.navy,
            border: isAuthenticated ? undefined : `2px solid ${theme.colors.border}`,
            borderRadius: isAuthenticated ? undefined : theme.borderRadius.full,
            fontSize: theme.fontSize.md,
            fontWeight: isAuthenticated ? undefined : theme.fontWeight.bold,
            cursor: 'pointer',
            width: 'fit-content',
            transition: `all ${theme.transitions.fast}`,
          }}
          onMouseEnter={!isAuthenticated ? (e => {
            e.currentTarget.style.border = `2px solid ${theme.colors.teal}`;
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = theme.shadows.button;
          }) : undefined}
          onMouseLeave={!isAuthenticated ? (e => {
            e.currentTarget.style.border = `2px solid ${theme.colors.border}`;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }) : undefined}
        >
          {isAuthenticated ? (
            <>
              Let's Begin <ArrowRight size={18} />
            </>
          ) : (
            <>
              Sign in to Begin <LogIn size={18} color={theme.colors.teal} />
            </>
          )}
        </button>
      </motion.div>
    </motion.div>
  );
}
